import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { PriceDiffDisplay } from "components/common/plan/MixPriceDiff";
import { useTenantData } from "contexts/tenant";
import { fetchErrorDetail } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useMemo, useState } from "react";
import {
  generatedApi,
  HeatOverrideCatalogCreate,
  HeatRecipe,
  HeatQueueEntry,
  useCreateCompletedHeatsOverrideMutation,
  useCreateHeatOverrideMutation,
  useListHeatOverrideReasonsQuery,
  useListHeatOverrideRecipesQuery,
} from "src/store/api/generatedApi";
import { useAppDispatch } from "src/store/store";
import { OverrideRecipeCard } from "./OverrideRecipeCard";
import {
  OverrideReasonFields,
  subreasonOptionsFor,
} from "./OverrideReasonFields";
import {
  getOverrideCostDelta,
  useOverrideCostSummaryMap,
} from "./useOverrideCostSummaries";

const MAX_LISTED_HEATS = 4;

const OverrideSummary = ({
  name,
  subtitle,
  costDeltaPerTonne,
}: {
  name: string;
  subtitle: string;
  costDeltaPerTonne: number | null;
}) => {
  const { t } = useTenantTranslation();
  return (
    <Box sx={{ border: 1, borderColor: "grey.300", borderRadius: 1, p: 1.5 }}>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {t("overrideNewRecipe")}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: "fontWeightBold" }}>
        {name}
      </Typography>
      <Typography variant="body2Mono" sx={{ color: "text.secondary" }}>
        {subtitle}
      </Typography>
      {costDeltaPerTonne !== null && (
        <Stack direction="row" sx={{ gap: 1, alignItems: "center", mt: 1 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("overrideCostChange")}
          </Typography>
          <PriceDiffDisplay
            priceDiff={costDeltaPerTonne}
            unitType="specific_cost"
          />
        </Stack>
      )}
    </Box>
  );
};

interface Props {
  open: boolean;
  doClose: () => void;
  onOverrideCreated: () => void;
  selectedHeats: Pick<HeatQueueEntry, "heat_id" | "stable_heat_id">[];
  steelGrade: string;
  floorRecipe: HeatRecipe | null;
  heatTiming: OverrideHeatTiming;
}

export type OverrideHeatTiming = "upcoming" | "completed";

type Step = "recipe" | "reason";

// null = the floor recipe (leftmost card); creating an override with it is a
// no-op, so it can be shown and highlighted but never advances the flow.
type RecipeSelection = number | null;

export const OverrideRecipeDialog = ({
  open,
  doClose,
  onOverrideCreated,
  selectedHeats,
  steelGrade,
  floorRecipe,
  heatTiming,
}: Props) => {
  const { t, i18n } = useTenantTranslation();
  const tenantName = useTenant();
  const { heat_override_options: heatOverrideOptions } = useTenantData();
  const commentRequired = heatOverrideOptions.includes("comment_required");
  const dispatch = useAppDispatch();

  const [step, setStep] = useState<Step>("recipe");
  const [selectedMixId, setSelectedMixId] = useState<RecipeSelection>(null);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [selectedSubreason, setSelectedSubreason] = useState<string | null>(
    null
  );
  const [comment, setComment] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const recipes = useListHeatOverrideRecipesQuery(
    { tenantName },
    { skip: !open }
  );
  const reasons = useListHeatOverrideReasonsQuery(
    { tenantName },
    { skip: !open }
  );
  const [createUpcomingOverride, createUpcomingState] =
    useCreateHeatOverrideMutation();
  const [createCompletedOverride, createCompletedState] =
    useCreateCompletedHeatsOverrideMutation();
  const createState =
    heatTiming === "completed" ? createCompletedState : createUpcomingState;
  const { costByMixId } = useOverrideCostSummaryMap();

  const recipeItems = useMemo(
    () =>
      (recipes.data?.items ?? []).filter(
        (item) => item.steel_grades?.includes(steelGrade) ?? true
      ),
    [recipes.data, steelGrade]
  );

  const selectedRecipe = useMemo(
    () => recipeItems.find((item) => item.mix_id === selectedMixId),
    [recipeItems, selectedMixId]
  );
  const reasonOptions = reasons.data?.reasons ?? [];
  const requiresSubreason =
    subreasonOptionsFor({ reasonOptions, reason: selectedReason }).length > 0;

  const floorCost =
    floorRecipe === null ? undefined : costByMixId.get(floorRecipe.mix.id);

  const costDeltaPerTonne = (mixId: number): number | null =>
    heatTiming === "completed"
      ? null
      : getOverrideCostDelta({
          overrideCost: costByMixId.get(mixId),
          replacedCost: floorCost,
        });

  const selectedCostDeltaPerTonne =
    selectedRecipe === undefined
      ? null
      : costDeltaPerTonne(selectedRecipe.mix_id);
  const overrideSubtitle =
    floorRecipe === null
      ? steelGrade
      : `${steelGrade} · ${t("overrideReplaces", { recipe: floorRecipe.name })}`;

  const heatScope = useMemo(() => {
    const ordered = selectedHeats
      .map((heat) => heat.heat_id)
      .sort((a, b) => a - b);
    if (ordered.length === 1) {
      return t("overrideAppliesToHeat", { heatId: ordered[0] });
    }
    if (ordered.length <= MAX_LISTED_HEATS) {
      const heats = new Intl.ListFormat(i18n.language, {
        type: "conjunction",
      }).format(ordered.map(String));
      return t("overrideAppliesToHeats", { heats });
    }
    return t("overrideAppliesToHeatCount", { count: ordered.length });
  }, [selectedHeats, t, i18n.language]);

  const reset = () => {
    setStep("recipe");
    setSelectedMixId(null);
    setSelectedReason(null);
    setSelectedSubreason(null);
    setComment("");
    setSubmitError(null);
  };

  const handleClose = () => {
    reset();
    doClose();
  };

  const reasonComplete =
    selectedReason !== null &&
    (!requiresSubreason || selectedSubreason !== null);
  const recipeStepComplete = selectedRecipe !== undefined;
  const commentComplete = !commentRequired || comment.trim().length > 0;
  const canSubmit =
    recipeStepComplete &&
    reasonComplete &&
    commentComplete &&
    floorRecipe !== null;

  const handleSubmit = async () => {
    if (
      floorRecipe === null ||
      selectedReason === null ||
      selectedRecipe === undefined
    ) {
      return;
    }
    const create: HeatOverrideCatalogCreate = {
      mode: "catalog",
      catalog: {
        fixed_mix_set_id: selectedRecipe.fixed_mix_set_id,
        mix_id: selectedRecipe.mix_id,
      },
      stable_heat_ids: selectedHeats.map((heat) => heat.stable_heat_id),
      reason: selectedReason,
      subreason: requiresSubreason ? selectedSubreason : null,
      comment: comment === "" ? null : comment,
      baseline_mix_id: floorRecipe.mix.id,
    };
    setSubmitError(null);
    const result =
      heatTiming === "completed"
        ? await createCompletedOverride({
            tenantName,
            heatOverrideCatalogCreate: create,
          })
        : await createUpcomingOverride({ tenantName, create });
    if ("data" in result) {
      dispatch(generatedApi.util.invalidateTags(["deployments", "heat_queue"]));
      reset();
      onOverrideCreated();
    } else {
      setSubmitError(
        fetchErrorDetail(result.error) ?? t("overrideCreateError")
      );
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <span>{t("editRecipe")}</span>
        <Typography
          variant="body2"
          component="span"
          sx={{ color: "text.secondary", fontWeight: "fontWeightRegular" }}
        >
          {heatScope}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack sx={{ gap: 2, pt: 1 }}>
          {step === "recipe" ? (
            <Stack sx={{ gap: 1 }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {t("selectOverrideRecipe", { steelGrade })}
              </Typography>
              <Stack
                role="radiogroup"
                aria-label={t("editRecipe")}
                sx={{ gap: 1.5 }}
              >
                {floorRecipe !== null && (
                  <OverrideRecipeCard
                    title={floorRecipe.name}
                    subtitle={`${steelGrade} · ${t(
                      heatTiming === "completed"
                        ? "overrideRecipeRanOn"
                        : "overrideRecipeCurrent"
                    )}`}
                    mix={floorRecipe.mix}
                    costDeltaPerTonne={null}
                    selected={selectedMixId === null}
                    onSelect={() => {
                      setSelectedMixId(null);
                    }}
                  />
                )}
                {recipeItems.map((recipe) => (
                  <OverrideRecipeCard
                    key={recipe.mix_id}
                    title={recipe.name}
                    subtitle={steelGrade}
                    mix={recipe.mix}
                    costDeltaPerTonne={costDeltaPerTonne(recipe.mix_id)}
                    selected={selectedMixId === recipe.mix_id}
                    onSelect={() => {
                      setSelectedMixId(recipe.mix_id);
                    }}
                  />
                ))}
              </Stack>
              {recipes.isSuccess && recipeItems.length === 0 && (
                <Alert severity="info">{t("noOverrideRecipes")}</Alert>
              )}
            </Stack>
          ) : (
            <Stack sx={{ gap: 2 }}>
              {selectedRecipe !== undefined && (
                <OverrideSummary
                  name={selectedRecipe.name}
                  subtitle={overrideSubtitle}
                  costDeltaPerTonne={selectedCostDeltaPerTonne}
                />
              )}
              <OverrideReasonFields
                reasonOptions={reasonOptions}
                reason={selectedReason}
                subreason={selectedSubreason}
                comment={comment}
                commentRequired={commentRequired}
                onReasonChange={(reason) => {
                  setSelectedReason(reason);
                  setSelectedSubreason(null);
                }}
                onSubreasonChange={setSelectedSubreason}
                onCommentChange={setComment}
              />
              {floorRecipe === null && (
                <Alert severity="warning">{t("overrideNoStandingPlan")}</Alert>
              )}
            </Stack>
          )}
          {submitError !== null && (
            <Alert severity="error">{submitError}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {step === "recipe" ? (
          <>
            <Button onClick={handleClose}>{t("cancel")}</Button>
            <Button
              variant="contained"
              disabled={!recipeStepComplete}
              onClick={() => {
                setStep("reason");
              }}
            >
              {t("next")}
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => {
                setStep("recipe");
              }}
            >
              {t("back")}
            </Button>
            {!reasonComplete && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", ml: "auto" }}
              >
                {selectedReason === null
                  ? t("overrideReasonRequiredHint")
                  : t("overrideSubreasonRequiredHint")}
              </Typography>
            )}
            <LoadingButton
              variant="contained"
              loading={createState.isLoading}
              disabled={!canSubmit}
              onClick={() => {
                void handleSubmit();
              }}
            >
              {t("confirmOverride")}
            </LoadingButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
